import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { X } from '@phosphor-icons/react';

/**
 * Фото места на весь экран, листаются свайпом.
 *
 * Лента — нативная прокрутка с привязкой к кадру (`scroll-snap`), а не
 * своя физика жеста: так инерция и сила свайпа остаются системными
 * (PROJECT_STRUCTURE, «Горизонтальные ленты»). Раньше фото открывалось
 * по одному, и к соседнему приходилось закрывать и тапать снова.
 *
 * Закрывается крестиком, тапом мимо снимка, «Назад» Telegram и Escape —
 * последние два вешает страница (эффект `lightboxOpen`)
 */
const PhotoViewer = ({ images, startIndex, onClose }) => {
  const scrollerRef = useRef(null);

  // Открываемся сразу на нужном снимке, без прокрутки на глазах
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollLeft = startIndex * el.clientWidth;
  }, [startIndex]);

  return (
    <div className="fixed inset-0 z-modal animate-fadeIn" style={{ background: 'var(--color-scrim)' }}>
      <div
        ref={scrollerRef}
        className="flex h-full w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden"
        style={{ scrollbarWidth: 'none', overscrollBehaviorX: 'contain' }}
      >
        {images.map((img, i) => (
          <PhotoSlide
            key={img.id ?? i}
            url={img.url}
            number={i + 1}
            total={images.length}
            onClose={onClose}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Кадр просмотра: снимок вписан в свободное место целиком — и уменьшается,
 * и увеличивается. Раньше у картинки были только max-width/max-height:
 * она могла сжиматься, но не расти, и на iOS снимок с небольшим файлом
 * показывался в натуральном размере — вдвое мельче соседнего того же
 * формата.
 *
 * Счётчик и крестик — строкой прямо над снимком, по его ширине: рядом
 * с фото, но не поверх него (поверх закрывали кадр) и не у края экрана
 * (там были далеко от снимка). Тап по полю вокруг снимка закрывает,
 * по снимку — нет
 */
// Строка счётчика и крестика над снимком: кнопка 44px + зазор 8px
const PHOTO_BAR_H = 52;
const PhotoSlide = ({ url, number, total, onClose }) => {
  const boxRef = useRef(null);
  const [box, setBox] = useState(null);
  const [natural, setNatural] = useState(null);

  // Свободное место под снимком: высота экрана минус отступ под кнопки
  // Telegram сверху. Следим за ним — вьюпорт меняется при развороте
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Вертикальные снимки — все в кадре 4:5 (обрезка по краям, object-cover):
  // у загруженных фото разные пропорции, 3:4, 9:16, 2:3, и при листании
  // кадр прыгал по высоте. Горизонтальные — в своих пропорциях
  const size = box && natural
    ? (() => {
      const ratio = natural.h > natural.w ? 4 / 5 : natural.w / natural.h;
      const w = Math.min(box.w, (box.h - PHOTO_BAR_H) * ratio);
      return { w: Math.round(w), h: Math.round(w / ratio) };
    })()
    : null;

  // Из кеша снимок бывает загружен раньше, чем React повесит onLoad.
  // Колбэк постоянный и пишет размер, только если он новый: новая функция
  // ref на каждой перерисовке вызывалась заново и зацикливала обновления
  const readNatural = useCallback((el) => {
    if (!el || !el.complete || !el.naturalWidth) return;
    setNatural((prev) => (
      prev && prev.w === el.naturalWidth && prev.h === el.naturalHeight
        ? prev
        : { w: el.naturalWidth, h: el.naturalHeight }
    ));
  }, []);

  return (
    <div
      onClick={onClose}
      className="flex h-full w-full shrink-0 snap-center flex-col px-4"
      style={{
        paddingTop: 'calc(var(--safe-area-top, 0px) + var(--content-safe-area-top, 0px) + 8px)',
        paddingBottom: 'max(24px, var(--safe-area-bottom, 0px), env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div ref={boxRef} className="flex min-h-0 flex-1 items-center justify-center">
        <div
          className="flex flex-col"
          // До замера рамки и снимка — невидимо, без прыжка размера
          style={size ? { width: size.w } : { width: 1, visibility: 'hidden' }}
        >
          <div className="flex items-center justify-between" style={{ height: PHOTO_BAR_H - 8, marginBottom: 8 }}>
            {total > 1 ? (
              <span
                onClick={(e) => e.stopPropagation()}
                className="rounded-full bg-theme-card px-3 py-1 text-body-sm text-theme-text-primary tabular"
              >
                {number} / {total}
              </span>
            ) : <span />}
            <button
              onClick={onClose}
              aria-label="Закрыть"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-theme-border bg-theme-card text-theme-text-primary"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X size={20} weight="bold" />
            </button>
          </div>
          <img
            ref={readNatural}
            src={url}
            alt=""
            onLoad={(e) => readNatural(e.target)}
            onClick={(e) => e.stopPropagation()}
            className="block rounded-md object-cover"
            style={size ? { width: size.w, height: size.h } : { width: 1, height: 1 }}
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
};

export default PhotoViewer;
